import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import { NewLoginForm } from './NewLoginDailogForm';
import { BackDropLoad } from './LoadingBackDrop';
import { UserContext } from './userContext';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

function slugify(s) {
  return String(s || '').toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
}

/* ─────────── Inline icons ─────────── */

const Icon = {
  Briefcase: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Search: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  ),
  ChevronDown: ({ className, color = 'currentColor' }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
};

/* ─────────── Hero ─────────── */

function CareersHero() {
  return (
    <section
      className="
        relative isolate overflow-hidden mt-3 mb-6
        rounded-2xl border border-border-subtle
        bg-gradient-to-br from-surface-paper to-surface-elevated
      "
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
        style={{ background: 'radial-gradient(900px 400px at 10% -10%, rgba(251,191,36,0.16), transparent 60%), radial-gradient(700px 360px at 100% 110%, rgba(251,191,36,0.08), transparent 60%)' }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(46,46,52,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(46,46,52,0.6) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at 50% 50%, #000, transparent 85%)',
        }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />

      <div className="px-5 sm:px-7 md:px-9 py-7 sm:py-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
          <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber">Opportunities</span>
        </div>
        <h1 className="text-[28px] sm:text-[34px] md:text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-fg-primary">
          Careers &amp; Openings
        </h1>
        <div aria-hidden className="mt-3.5 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
        <p className="mt-3.5 max-w-[640px] text-[14px] leading-[1.6] text-fg-secondary">
          Job openings across public and private sectors in the construction industry.
        </p>
      </div>
    </section>
  );
}

/* ─────────── Filter dropdown ─────────── */

function FilterSelect({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current && btnRef.current.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const opt = options[active];
        if (opt) { onChange(opt.value); setOpen(false); btnRef.current && btnRef.current.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, active, options, onChange]);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div ref={ref} className="relative w-full sm:w-auto">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`
          relative flex h-10 w-full sm:min-w-[160px] items-center gap-2 rounded-lg pl-3 pr-9
          bg-surface-overlay/70
          border
          text-[12.5px] text-left
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          ${open
            ? 'border-amber bg-surface-overlay shadow-[0_0_0_3px_rgba(251,191,36,0.22)]'
            : 'border-amber/22 hover:border-amber/55'}
        `}
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber shrink-0">{label}</span>
        <span className="min-w-0 flex-1 truncate font-medium text-fg-primary">{selected?.label}</span>
        <span className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 transition-[color,transform] duration-200 ${open ? 'rotate-180 text-amber' : 'text-amber/80'}`}>
          <Icon.ChevronDown className="h-3.5 w-3.5" />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className="
            absolute left-0 right-0 top-full z-30 mt-2
            max-h-72 overflow-auto rounded-xl
            border border-amber/30 bg-surface-elevated
            shadow-dialog
            animate-[fadeIn_140ms_ease-out]
            min-w-[200px] py-1
          "
        >
          {options.map((opt, i) => {
            const isSel = opt.value === value;
            return (
              <button
                type="button"
                key={opt.value}
                role="option"
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`
                  flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left text-[13px]
                  transition-[background-color,color] duration-150 ease-out-soft
                  ${isSel
                    ? 'bg-amber/14 text-amber font-semibold'
                    : i === active
                      ? 'bg-amber/8 text-fg-primary'
                      : 'text-fg-secondary hover:bg-amber/8 hover:text-fg-primary'}
                `}
              >
                <span className="truncate">{opt.label}</span>
                {isSel && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0 text-amber" aria-hidden>
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────── Filter bar ─────────── */

function FilterBar({ search, setSearch, type, setType, company, setCompany, sort, setSort, types, companies, total, onClear, anyActive }) {
  return (
    <div className="mb-5 flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {/* Search */}
        <label className="relative w-full sm:flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-amber/80">
            <Icon.Search className="h-4 w-4" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs by title or company…"
            className="
              h-10 w-full rounded-lg pl-9 pr-3 text-[13px] text-fg-primary
              bg-surface-overlay/70 placeholder:text-fg-muted
              border border-amber/22
              outline-none
              transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
              hover:border-amber/55
              focus:border-amber focus:bg-surface-overlay focus:shadow-[0_0_0_3px_rgba(251,191,36,0.22)]
            "
          />
        </label>

        <FilterSelect
          label="Type"
          value={type}
          onChange={setType}
          options={[{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]}
        />
        <FilterSelect
          label="Company"
          value={company}
          onChange={setCompany}
          options={[{ value: 'all', label: 'All companies' }, ...companies.map((c) => ({ value: c, label: c }))]}
        />
        <FilterSelect
          label="Sort"
          value={sort}
          onChange={setSort}
          options={[
            { value: 'newest', label: 'Newest first' },
            { value: 'oldest', label: 'Oldest first' },
          ]}
        />
      </div>

      {/* Active filters strip */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-fg-tertiary">
          Showing <span className="text-fg-primary font-semibold">{total}</span> {total === 1 ? 'opening' : 'openings'}
        </div>
        {anyActive && (
          <button
            type="button"
            onClick={onClear}
            className="
              inline-flex items-center gap-1 rounded-md px-2 py-1
              text-[11px] font-bold uppercase tracking-[0.14em] text-fg-tertiary
              hover:text-amber hover:bg-amber/8
              transition-colors duration-150
            "
          >
            <Icon.Close className="h-3 w-3" />
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────── Job card - sleek 2026 ─────────── */

function JobCard({ job }) {
  const ctaLabel = job?.type === '1' ? 'View details' : 'Apply now';
  const meta = [
    job?.job_type,
    job?.current_openings ? `${job.current_openings} open` : null,
  ].filter(Boolean);

  return (
    <RouterLink
      to={`/job-details/${slugify(job?.job_title)}/${job?.job_id}`}
      title={job?.job_title}
      className="
        group relative flex h-full flex-col overflow-hidden rounded-2xl
        border border-border-subtle bg-surface-paper
        transition-[transform,border-color,box-shadow] duration-300 ease-out-soft
        hover:-translate-y-1 hover:border-amber hover:shadow-amber-glow-strong
      "
    >
      {/* Top amber bar - slides in from left on hover */}
      <span
        aria-hidden
        className="
          pointer-events-none absolute inset-x-0 top-0 h-[3px]
          bg-gradient-to-r from-amber via-amber-light to-amber
          origin-left scale-x-0
          transition-transform duration-500 ease-out-soft
          group-hover:scale-x-100
        "
      />

      {/* Top-right corner amber glow on hover */}
      <div
        aria-hidden
        className="
          pointer-events-none absolute -top-px -right-px h-28 w-28
          opacity-0 transition-opacity duration-500 ease-out-soft
          group-hover:opacity-100
        "
        style={{ background: 'radial-gradient(closest-side, rgba(251,191,36,0.22), transparent 75%)' }}
      />

      <div className="flex flex-1 flex-col p-4">
        {/* Pulsing dot + company */}
        <div className="mb-2.5 flex items-center gap-2 min-w-0">
          <span aria-hidden className="relative inline-flex h-2 w-2 shrink-0">
            <span className="absolute inset-0 rounded-full bg-amber/45 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber" style={{ boxShadow: '0 0 8px rgba(251,191,36,0.7)' }} />
          </span>
          <span className="truncate text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber">
            {job?.posted_by}
          </span>
        </div>

        {/* Title */}
        <h3
          className="
            text-[15px] font-bold leading-[1.25] tracking-[-0.01em] text-fg-primary
            line-clamp-2
            transition-colors duration-200
            group-hover:text-white
          "
        >
          {job?.job_title}
        </h3>

        {/* Inline meta */}
        {meta.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-fg-secondary">
            {meta.map((m, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span aria-hidden className="text-fg-tertiary">·</span>}
                <span className="truncate">{m}</span>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Footer: hairline + date + arrow circle */}
        <div className="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between gap-2">
          <span className="text-[10.5px] font-medium text-fg-tertiary truncate">
            {job?.date || 'Recently posted'}
          </span>
          <span
            className="
              inline-flex h-7 w-7 items-center justify-center rounded-full
              border border-amber/30 bg-amber/12 text-amber shrink-0
              transition-[background-color,border-color,color,transform,box-shadow] duration-200 ease-out-soft
              group-hover:bg-amber group-hover:border-amber group-hover:text-ink
              group-hover:translate-x-0.5
              group-hover:shadow-[0_6px_14px_-4px_rgba(251,191,36,0.55)]
            "
            aria-label={ctaLabel}
            title={ctaLabel}
          >
            <Icon.ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </RouterLink>
  );
}

/* ─────────── Empty / No matches ─────────── */

function EmptyJobs({ filtered }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border-subtle bg-surface-paper/40 px-6 py-14 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-subtle bg-surface-overlay/60 text-fg-tertiary">
        <Icon.Briefcase className="h-6 w-6" />
      </div>
      <h3 className="text-[16px] font-semibold text-fg-primary">
        {filtered ? 'No jobs match your filters' : 'No openings right now'}
      </h3>
      <p className="mt-1.5 max-w-[380px] text-[13px] leading-relaxed text-fg-secondary">
        {filtered ? 'Try clearing filters or adjusting your search.' : 'Check back soon for new opportunities.'}
      </p>
    </div>
  );
}

/* ═══════════════════ Careers ═══════════════════ */

export const Careers = () => {
  const [loading, setLoading] = useState(true);
  const [careers, setCareers] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const { user } = useContext(UserContext);
  const [showLoginForm, setShowLoginForm] = useState(!user);
  const [wait, setWait] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [company, setCompany] = useState('all');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'careers' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({
        seo_title: 'Careers | ESAY',
        seo_desc: 'Job openings in public and private sectors in construction.',
        seo_keywords: 'construction jobs, engineering careers',
      });
    });
    Axios.get('/new_apis/get-careers', { headers: { key } }).then((res) => {
      setCareers(res.data.careers || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Unique values for dropdowns
  const types = useMemo(() => {
    const set = new Set();
    careers.forEach((c) => { if (c.job_type) set.add(c.job_type); });
    return Array.from(set).sort();
  }, [careers]);

  const companies = useMemo(() => {
    const set = new Set();
    careers.forEach((c) => { if (c.posted_by) set.add(c.posted_by); });
    return Array.from(set).sort();
  }, [careers]);

  // Filtered + sorted list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = careers.filter((c) => {
      if (q && !(`${c.job_title} ${c.posted_by}`.toLowerCase().includes(q))) return false;
      if (type !== 'all' && c.job_type !== type) return false;
      if (company !== 'all' && c.posted_by !== company) return false;
      return true;
    });
    const parseDate = (d) => {
      if (!d) return 0;
      const t = Date.parse(d);
      return Number.isNaN(t) ? 0 : t;
    };
    list.sort((a, b) => {
      const da = parseDate(a.date);
      const db = parseDate(b.date);
      return sort === 'newest' ? db - da : da - db;
    });
    return list;
  }, [careers, search, type, company, sort]);

  const anyActive = !!(search || type !== 'all' || company !== 'all' || sort !== 'newest');
  const onClear = () => { setSearch(''); setType('all'); setCompany('all'); setSort('newest'); };

  return (
    <>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          <meta name="description" content={metadata.seo_desc} />
          <meta name="keywords" content={metadata.seo_keywords} />
        </Helmet>
      )}

      <BackDropLoad wait={wait} />
      <NewLoginForm
        strict
        strictTitle="Sign in to view careers"
        strictMessage="Career listings are available to ESAY members. Log in to browse all openings."
        open={showLoginForm}
        showload={() => setWait(true)}
        hideload={() => setWait(false)}
        closeform={() => setShowLoginForm(false)}
        openloginform={() => setShowLoginForm(true)}
      />

      <PageContainer maxWidth="lg">
        <CareersHero />

        <section className="mt-2 mb-10">
          {!loading && (
            <FilterBar
              search={search} setSearch={setSearch}
              type={type} setType={setType}
              company={company} setCompany={setCompany}
              sort={sort} setSort={setSort}
              types={types} companies={companies}
              total={filtered.length}
              anyActive={anyActive}
              onClear={onClear}
            />
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border-subtle bg-surface-paper p-4">
                  <div className="h-2 w-20 rounded bg-surface-overlay/60 animate-pulse mb-2.5" />
                  <div className="h-3 w-3/4 rounded bg-surface-overlay/60 animate-pulse mb-1.5" />
                  <div className="h-3 w-1/2 rounded bg-surface-overlay/60 animate-pulse mb-3" />
                  <div className="h-2.5 w-2/3 rounded bg-surface-overlay/60 animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div
              className="transition-[filter] duration-300 ease-out-soft"
              style={user ? undefined : { filter: 'blur(4px)', pointerEvents: 'none' }}
            >
              {filtered.length === 0 ? (
                <EmptyJobs filtered={anyActive} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filtered.map((career) => (
                    <JobCard key={career.job_id} job={career} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </PageContainer>
    </>
  );
};
